import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../services/api.js';

const initialState = {
  user: null,
  records: [],
  total: 0,
  page: 1,
  limit: 25,
  historyRecords: [],
  historyTotal: 0,
  historyPage: 1,
  historyLimit: 25,
  loading: false,
  error: '',
  selectedRecord: null,
  viewMode: 'records',
  searchTerm: '',
  notifications: [],
  notificationsLoading: true,
  notificationsError: '',
};

export const fetchActiveRecords = createAsyncThunk(
  'collector/fetchActiveRecords',
  async (_, { getState, rejectWithValue }) => {
    const { collector } = getState();
    const userId = collector.user?.userId;
    if (!userId) {
      return rejectWithValue({ message: 'Missing user.' });
    }

    try {
      const res = await api.get('/records', {
        params: {
          collectorId: userId,
          page: collector.page,
          limit: collector.limit,
          search: collector.searchTerm.trim() || undefined,
          category: 'active',
        },
      });
      const { data, total } = res.data || {};
      return { data: data || [], total: total || 0 };
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message || 'Failed to fetch records.';
      return rejectWithValue({ status, message });
    }
  }
);

export const fetchHistoryRecords = createAsyncThunk(
  'collector/fetchHistoryRecords',
  async (_, { getState, rejectWithValue }) => {
    const { collector } = getState();
    const userId = collector.user?.userId;
    if (!userId) {
      return rejectWithValue({ message: 'Missing user.' });
    }

    try {
      const res = await api.get('/records', {
        params: {
          collectorId: userId,
          page: collector.historyPage,
          limit: collector.historyLimit,
          search: collector.searchTerm.trim() || undefined,
          category: 'history',
        },
      });
      const { data, total } = res.data || {};
      return { data: data || [], total: total || 0 };
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || 'Failed to fetch history.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  'collector/fetchNotifications',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/records/notifications');
      return res.data || [];
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to fetch notifications.';
      return rejectWithValue({ message });
    }
  }
);

export const fetchRecordById = createAsyncThunk(
  'collector/fetchRecordById',
  async (recordId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/records/${recordId}`);
      return res.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to load record.';
      return rejectWithValue({ message });
    }
  }
);

export const updateRecord = createAsyncThunk(
  'collector/updateRecord',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const { assignedCollector, comments, _id, __v, ...payload } = data;
      const res = await api.put(`/records/${id}`, payload);
      return res.data;
    } catch (err) {
      const message =
        err?.response?.data?.message || err?.message || 'Failed to update record.';
      return rejectWithValue({ message });
    }
  }
);

const collectorSlice = createSlice({
  name: 'collector',
  initialState,
  reducers: {
    setUser(state, action) {
      state.user = action.payload || null;
    },
    setPage(state, action) {
      state.page = action.payload;
    },
    setLimit(state, action) {
      state.limit = action.payload;
    },
    setHistoryPage(state, action) {
      state.historyPage = action.payload;
    },
    setHistoryLimit(state, action) {
      state.historyLimit = action.payload;
    },
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    setSelectedRecord(state, action) {
      state.selectedRecord = action.payload;
    },
    setViewMode(state, action) {
      state.viewMode = action.payload;
    },
    clearError(state) {
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveRecords.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchActiveRecords.fulfilled, (state, action) => {
        state.records = action.payload.data;
        state.total = action.payload.total;
        state.loading = false;
      })
      .addCase(fetchActiveRecords.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to fetch records.';
        state.loading = false;
      })
      .addCase(fetchHistoryRecords.pending, (state) => {
        state.loading = true;
        state.error = '';
      })
      .addCase(fetchHistoryRecords.fulfilled, (state, action) => {
        state.historyRecords = action.payload.data;
        state.historyTotal = action.payload.total;
        state.loading = false;
      })
      .addCase(fetchHistoryRecords.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to fetch history.';
        state.loading = false;
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.notificationsLoading = true;
        state.notificationsError = '';
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.notifications = action.payload;
        state.notificationsLoading = false;
        state.notificationsError = '';
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.notificationsLoading = false;
        state.notificationsError = action.payload?.message || 'Failed to fetch notifications.';
      })
      .addCase(fetchRecordById.fulfilled, (state, action) => {
        state.selectedRecord = action.payload;
      })
      .addCase(fetchRecordById.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to load record.';
      })
      .addCase(updateRecord.fulfilled, (state, action) => {
        state.selectedRecord = action.payload;
      })
      .addCase(updateRecord.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update record.';
      });
  },
});

export const {
  setUser,
  setPage,
  setLimit,
  setHistoryPage,
  setHistoryLimit,
  setSearchTerm,
  setSelectedRecord,
  setViewMode,
  clearError,
} = collectorSlice.actions;

export const selectCollectorState = (state) => state.collector;
export const selectCollectorUser = (state) => state.collector.user;
export const selectCollectorRecords = (state) => state.collector.records;
export const selectCollectorTotal = (state) => state.collector.total;
export const selectCollectorPage = (state) => state.collector.page;
export const selectCollectorLimit = (state) => state.collector.limit;
export const selectCollectorHistoryRecords = (state) => state.collector.historyRecords;
export const selectCollectorHistoryTotal = (state) => state.collector.historyTotal;
export const selectCollectorHistoryPage = (state) => state.collector.historyPage;
export const selectCollectorHistoryLimit = (state) => state.collector.historyLimit;
export const selectCollectorLoading = (state) => state.collector.loading;
export const selectCollectorError = (state) => state.collector.error;
export const selectCollectorSelectedRecord = (state) => state.collector.selectedRecord;
export const selectCollectorViewMode = (state) => state.collector.viewMode;
export const selectCollectorSearchTerm = (state) => state.collector.searchTerm;
export const selectCollectorNotifications = (state) => state.collector.notifications;
export const selectCollectorNotificationsLoading = (state) =>
  state.collector.notificationsLoading;
export const selectCollectorNotificationsError = (state) =>
  state.collector.notificationsError;

export default collectorSlice.reducer;
